import React, { useMemo, useRef } from 'react'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import TourDealPriceToggle from './TourDealPriceToggle'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import styled from 'styled-components'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

interface SortOption {
  label: string
  value: 'price.asc' | 'price.desc' | 'duration.asc' | 'duration.desc' | 'countries.asc' | 'countries.desc'
}

const sortOptions: Array<SortOption> = [
  { label: 'Price (Lowest)', value: 'price.asc' },
  { label: 'Price (Highest)', value: 'price.desc' },
  { label: 'Duration (Shortest)', value: 'duration.asc' },
  { label: 'Duration (Longest)', value: 'duration.desc' },
  { label: 'Number of countries (Lowest)', value: 'countries.asc' },
  { label: 'Number of countries (Highest)', value: 'countries.desc' },
]

const SortWrapper = styled(Group)`
  padding-top: ${rem(8)};
  padding-bottom: ${rem(2)};
`

interface Props {
  totalNumberOfOffers: number
  selectedOption: string
  onChange: (value: string) => void
}

function TourDealSortHeader(props: Props) {
  const { totalNumberOfOffers, selectedOption, onChange } = props
  const [open, toggleSort, _, closeSort] = useToggle()

  const triggerRef = useRef<HTMLButtonElement>(null)
  const selected = useMemo(() =>
    sortOptions.find(o => o.value === selectedOption) ?? sortOptions[0],
  [selectedOption])

  const onSortChange = (value: string) => {
    onChange(value)
    closeSort()
  }

  if (totalNumberOfOffers <= 0) {
    return null
  }

  return (
    <SortWrapper direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
      <BodyText variant="large">
        Showing {pluralizeToString('tour', totalNumberOfOffers)}
      </BodyText>
      <Group direction="horizontal" gap={8}>
        <AssistChip
          variant="filled"
          size="medium"
          onClick={toggleSort}
          endIcon={<DropdownChevron open={open} />}
          ref={triggerRef}
        >
          Sort: {selected?.label}
        </AssistChip>
        <DropdownList
          size="S"
          open={open}
          onClose={closeSort}
          anchorRef={triggerRef}
          triggerRef={triggerRef}
          data-testid="sort-options-dropdown"
        >
          <RadioGroup direction="vertical" gap={16}>
            {sortOptions.map(option => <RadioInput
              key={option.value}
              checked={selected?.value === option.value}
              onChange={() => onSortChange(option.value)}
              name="tourDealSortHeader"
              value={option.value}
            >
              {option.label}
            </RadioInput>)}
          </RadioGroup>
        </DropdownList>
        <TourDealPriceToggle />
      </Group>
    </SortWrapper>
  )
}

export default TourDealSortHeader
